<template>
  <div class="row justify-content-md-center m-0">
    <b-col
      :cols="reduced? 12 : 10"
      :xl="reduced? 12 : 7"
    >
      <div
        v-if="isLoading"
        class="h-100 align-items-center d-flex justify-content-center card-body"
      >
        <div class="loader mx-auto" />
      </div>
      <div
        class="card mt-5"
        v-else
      >
        <div
          class="card-body"
        >
          <h4>
            INVOICING DETAILS
          </h4>

          <InvoicingSelection
            :is-managed-campaign="isManagedCampaign"
            :invoicing-lists="invoicingLists"
            @setInvoicingDetails="setInvoicingDetails"
            @resetDetailsForm="resetDetailsForm"
            @updateDetailsSelected="updateDetailsSelected"
            @updateSelectionType="updateSelectionType"
            :is-live="isLive"
          />
          <div
            class="row"
            v-if="!showDetailsForm"
          >
            <div
              class="col-md-12"
            >
              <div class="text-muted text-center mb-2">
                Please make a selection from the above.
              </div>
            </div>
          </div>
          <div
            class="row"
            v-else
          >
            <div
              class="col-md-12"
              v-if="isManagedCampaign"
            >
              <b-form-group
                id="fieldset-xero-id"
                label-cols-lg="3"
                label="Xero ID"
                label-class="form-label"
                label-for="xero-id-input"
                label-align-sm="right"
              >
                <b-form-input
                  id="xero-id-input"
                  v-model="xeroId"
                  trim
                  disabled
                  data-test="xero-id-input"
                />
              </b-form-group>
            </div>
            <div
              class="col-md-12"
            >
              <b-form-group
                id="fieldset-currency"
                label-cols-lg="3"
                label="Campaign Currency"
                label-class="form-label"
                label-for="currency-input"
                label-align-sm="right"
              >
                <b-form-input
                  id="currency-input"
                  v-model="getCampaignCurrency"
                  disabled
                  data-test="currency-input"
                />
              </b-form-group>
            </div>
            <div class="col-md-12">
              <b-form-group
                id="fieldset-alias"
                label-cols-lg="3"
                label-class="form-label"
                label-align-sm="right"
                invalid-feedback="Cannot be blank"
                :state="isCompanyNameValid"
              >
                <template
                  slot="label"
                  label-for="alias-input"
                >
                  Company Name <span class="form-required">*</span>
                </template>
                <b-form-input
                  id="alias-input"
                  v-model="alias"
                  trim
                  data-test="alias-input"
                  :disabled="isLive || isCampaignReadonly"
                />
              </b-form-group>
            </div>
            <div class="col-md-12">
              <b-form-group
                id="fieldset-address-line-1"
                label-cols-lg="3"
                label-class="form-label"
                label-align-sm="right"
                invalid-feedback="Cannot be blank"
                :state="isAddressLine1Valid"
              >
                <template
                  slot="label"
                  label-for="address-line-input-1"
                >
                  Address line 1 <span class="form-required">*</span>
                </template>
                <b-form-input
                  id="address-line-input-1"
                  v-model="addressLine1"
                  trim
                  required
                  data-test="address-line-input-1"
                  :disabled="isLive || isCampaignReadonly"
                />
              </b-form-group>
            </div>
            <div class="col-md-12">
              <b-form-group
                id="fieldset-address-line-2"
                label-cols-lg="3"
                label="Address line 2"
                label-class="form-label"
                label-for="address-line-input-2"
                label-align-sm="right"
              >
                <b-form-input
                  id="address-line-input-2"
                  v-model="addressLine2"
                  trim
                  data-test="address-line-input-2"
                  :disabled="isLive || isCampaignReadonly"
                />
              </b-form-group>
            </div>
            <div class="col-md-12">
              <b-form-group
                id="fieldset-address-town-city"
                label-cols-lg="3"
                label-class="form-label"
                label-align-sm="right"
                invalid-feedback="Cannot be blank"
                :state="isAddressTownCityValid"
              >
                <template
                  slot="label"
                  label-for="address-town-city-input"
                >
                  Town/City <span class="form-required">*</span>
                </template>
                <b-form-input
                  id="address-town-city-input"
                  v-model="addressTownCity"
                  trim
                  data-test="address-town-city-input"
                  :disabled="isLive || isCampaignReadonly"
                />
              </b-form-group>
            </div>
            <div class="col-md-12">
              <b-form-group
                id="fieldset-address-country"
                label-cols-lg="3"
                label-class="form-label"
                label-align-sm="right"
                invalid-feedback="Cannot be blank"
                :state="isAddressCountryIdValid"
              >
                <template
                  slot="label"
                  label-for="address-country-select"
                >
                  Country <span class="form-required">*</span>
                </template>
                <b-form-select
                  id="address-country-select"
                  v-model="addressCountryId"
                  :options="countries"
                  data-test="address-country-select"
                  :disabled="isLive || isCampaignReadonly"
                />
              </b-form-group>
            </div>
            <div class="col-md-12">
              <b-form-group
                id="fieldset-address-postal-code"
                label-cols-lg="3"
                label-class="form-label"
                label-align-sm="right"
                invalid-feedback="Cannot be blank"
                :state="isAddressPostalCodeValid"
              >
                <template
                  slot="label"
                  label-for="address-postal-code-input"
                >
                  Postal Code <span class="form-required">*</span>
                </template>
                <b-form-input
                  id="address-postal-code-input"
                  v-model="addressPostalCode"
                  trim
                  data-test="address-postal-code-input"
                  :disabled="isLive || isCampaignReadonly"
                />
              </b-form-group>
            </div>
            <div
              class="col-md-12"
            >
              <b-form-group
                id="fieldset-address-po-number"
                label-cols-lg="3"
                label="PO number"
                label-class="form-label"
                label-for="po-number-input"
                label-align-sm="right"
              >
                <b-form-input
                  id="po-number-input"
                  v-model="poNumber"
                  trim
                  data-test="po-number-input"
                  :disabled="isCampaignReadonly"
                />
              </b-form-group>
            </div>
            <div class="col-sm-12">
              <b-form-group
                id="fieldset-company-registration-number"
                label-cols-lg="4"
                label-class="form-label"
                label-align-sm="right"
                invalid-feedback="Cannot be blank"
                :state="isCompanyRegistrationNumberValid"
              >
                <template
                  slot="label"
                  label-for="company-registration-number-input"
                >
                  Company Registration Number <span class="form-required">*</span>
                </template>
                <b-form-input
                  id="company-registration-number-input"
                  v-model="companyRegistrationNumber"
                  trim
                  data-test="company-registration-number-input"
                  :disabled="isLive || isCampaignReadonly"
                />
              </b-form-group>
            </div>
            <div class="col-md-12">
              <div class="card">
                <div class="card-header">
                  <h3 class="card-title">
                    Primary accounts contact
                  </h3>
                </div>

                <div class="card-body">
                  <fieldset
                    class="form-fieldset"
                  >
                    <div class="row">
                      <div class="col-md-6">
                        <b-form-group
                          id="fieldset-accounts-contact-first-name"
                          label-class="form-label"
                          invalid-feedback="Cannot be blank"
                          :state="isAccountsContactFirstNameValid"
                        >
                          <template
                            slot="label"
                            label-for="accounts-contact-first-name-input"
                          >
                            First Name <span class="form-required">*</span>
                          </template>
                          <b-form-input
                            id="accounts-contact-first-name-input"
                            type="text"
                            v-model="contactFirstName"
                            trim
                            data-test="accounts-contact-first-name-input"
                            :disabled="isLive || isCampaignReadonly"
                          />
                        </b-form-group>
                      </div>
                      <div class="col-md-6">
                        <b-form-group
                          id="fieldset-accounts-contact-last-name"
                          label-class="form-label"
                        >
                          <template
                            slot="label"
                            label-for="accounts-contact-last-name-input"
                          >
                            Last Name
                          </template>
                          <b-form-input
                            id="accounts-contact-last-name-input"
                            type="text"
                            v-model="contactLastName"
                            trim
                            data-test="accounts-contact-last-name-input"
                            :disabled="isLive || isCampaignReadonly"
                          />
                        </b-form-group>
                      </div>
                    </div>
                    <b-form-group
                      id="fieldset-accounts-contact-email"
                      class="mb-0"
                      label-class="form-label"
                      :state="isAccountsContactEmailValid"
                    >
                      <template
                        slot="label"
                        label-for="accounts-contact-email-input"
                      >
                        Email Address <span class="form-required">*</span>
                      </template>
                      <b-form-input
                        id="accounts-contact-email-input"
                        type="email"
                        v-model="accountsContactEmail"
                        trim
                        data-test="accounts-contact-email-input"
                        :state="isEmailValid(accountsContactEmail)"
                        :disabled="isLive || isCampaignReadonly"
                      />
                    </b-form-group>
                  </fieldset>
                </div>
              </div>
            </div>

            <div class="col-md-12">
              <div class="card">
                <div class="card-header">
                  <h3 class="card-title">
                    Additional accounts contacts
                  </h3>
                  <small class="ml-3">
                    ({{ additionalContactsTotal }}/5)
                  </small>
                  <div class="card-options">
                    <div
                      class="card-options-collapse px-3 py-2 btn-sm"
                      v-b-toggle="`additional-contacts`"
                    >
                      <i
                        class="when-closed fa far fa-angle-down"
                      />
                      <i
                        class="when-opened fa far fa-angle-up"
                      />
                    </div>
                  </div>
                </div>
                <b-collapse
                  v-model="additionalContactsVisible"
                  id="additional-contacts"
                  accordion="additional-contacts"
                >
                  <div class="card-body">
                    <div class="row">
                      <div
                        class="col-md-6 additional-contact-form"
                        v-for="contact in additionalContacts"
                        :key="getAdditionalContactsIndex(contact)"
                      >
                        <div class="form-label d-flex">
                          #{{ getAdditionalContactsIndex(contact) + 1 }} Contact
                          <button
                            v-if="getAdditionalContactsIndex(contact)!==0"
                            type="button"
                            class="btn btn-danger btn-sm ml-auto"
                            @click="removeAdditionalContact(contact)"
                            :disabled="isCampaignReadonly"
                          >
                            Remove
                          </button>
                        </div>
                        <fieldset
                          class="form-fieldset"
                        >
                          <div class="row">
                            <div class="col-md-6">
                              <div class="form-group">
                                <label class="form-label">First name</label>
                                <b-form-input
                                  :id="`additional-contact-${getAdditionalContactsIndex(contact)}-first-name-input`"
                                  class="form-control"
                                  type="text"
                                  v-model="contact.firstName"
                                  trim
                                  :disabled="isLive || isCampaignReadonly"
                                />
                              </div>
                            </div>
                            <div class="col-md-6">
                              <div class="form-group">
                                <label class="form-label">Last name</label>
                                <b-form-input
                                  :id="`additional-contact-${getAdditionalContactsIndex(contact)}-last-name-input`"
                                  class="form-control"
                                  type="text"
                                  v-model="contact.lastName"
                                  trim
                                  :disabled="isLive || isCampaignReadonly"
                                />
                              </div>
                            </div>
                          </div>
                          <div class="form-group mb-0">
                            <label class="form-label">Email<span class="form-required">*</span></label>
                            <b-form-input
                              :id="`additional-contact-${getAdditionalContactsIndex(contact)}-email-input`"
                              class="form-control"
                              type="email"
                              v-model="contact.email"
                              trim
                              :state="isEmailValid(contact.email)"
                              :disabled="isLive || isCampaignReadonly"
                            />
                          </div>
                        </fieldset>
                      </div>
                      <div
                        class="col-md-6 d-flex additional-contact-form"
                      >
                        <button
                          v-if="additionalContactsTotal < 5"
                          type="button"
                          class="btn btn-secondary m-auto"
                          :disabled="isLive || isCampaignReadonly"
                          @click="addAdditionalContact"
                        >
                          <i class="fa-light fa-plus mr-2" />Add Contact
                        </button>
                      </div>
                    </div>
                  </div>
                </b-collapse>
              </div>
            </div>

            <div
              class="col-md-12"
              v-if="!reduced"
            >
              <b-form-group
                id="fieldset-first-name"
                label-cols-lg="3"
                label="Your First Name"
                label-class="form-label"
                label-for="first-name-input"
                label-align-sm="right"
              >
                <b-form-input
                  id="first-name-input"
                  v-model="firstName"
                  disabled
                  trim
                  data-test="first-name-input"
                />
              </b-form-group>
            </div>
            <div
              class="col-md-12"
              v-if="!reduced"
            >
              <b-form-group
                id="fieldset-last-name"
                label-cols-lg="3"
                label="Your Last Name"
                label-class="form-label"
                label-for="last-name-input"
                label-align-sm="right"
                class="mb-0"
              >
                <b-form-input
                  id="last-name-input"
                  v-model="lastName"
                  disabled
                  trim
                  data-test="last-name-input"
                />
              </b-form-group>
            </div>
          </div>
          <div
            class="d-flex"
            v-if="reduced"
          >
            <b-button
              :disabled="!isValid"
              variant="primary"
              class="ml-auto"
              @click="updateInvoicingDetails"
              :class="{ 'btn-loading': isButtonLoading }"
              data-test="submit-invoicing-details-button"
            >
              Submit
            </b-button>
          </div>
          <div v-else>
            <div
              class="d-flex align-items-center my-5"
              v-if="!isManagedCampaign"
            >
              <span class="stamp stamp-md bg-cyan-darker mr-3">
                <i class="fa fal fa-file-search" />
              </span>
              <div>
                <h4 class="m-0">
                  Approvals and Invoicing
                </h4>
              </div>
            </div>
            <p v-if="!isManagedCampaign">
              All ads go through an approval process to make sure they're safe and appropriate for
              users. Each ad is reviewed for compliance with our advertising policies. If any
              issues are found, you're notified with policy information and what you can do to get
              your ad approved.<br>
              <br>
              This campaign will not begin until the content of creatives are approved. Approvals
              may take up to two business days.<br>
              <br>
              Invoices will be issued monthly in arrears.  Subject to credit checks, we reserve
              the right to request payment in advance.
            </p>
            <b-button
              :disabled="!isValid || isCampaignReadonly"
              block
              class="mt-5 campaign-invoicing-submit"
              variant="primary"
              size="lg"
              @click="submitDetails"
              :class="{ 'btn-loading': isButtonLoading }"
              data-test="campaign-invoicing-submit"
            >
              <template v-if="isManagedCampaign">
                Save
              </template>
              <template v-else>
                SUBMIT FOR APPROVAL
              </template>
            </b-button>
          </div>
        </div>
      </div>
    </b-col>
  </div>
</template>

<script>
import {
  mapActions, mapState, mapGetters, mapMutations,
} from 'vuex';
import * as types from '@/store/types';
import * as campaignTypes from '@/modules/campaigns/store/types';
import * as accountTypes from '@/modules/account/store/types';

import InvoicingSelection from '@/modules/account/components/InvoicingSelection';

export default {
  name: 'CampaignInvoicing',
  components: {
    InvoicingSelection,
  },
  props: {
    reduced: {
      type: Boolean,
      default: false,
    },
    activeIndex: {
      type: Number,
      default: 0,
    },
    editMode: {
      type: Boolean,
      default: false,
    },
    isLive: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      companyName: '',
      addressLine1: '',
      addressLine2: '',
      addressTownCity: '',
      addressCountryId: '',
      addressPostalCode: '',
      companyRegistrationNumber: '',
      contactFirstName: '',
      contactLastName: '',
      accountsContactEmail: null,
      firstName: '',
      lastName: '',
      jobTitle: '',
      poNumber: null,
      xeroId: '',
      alias: '',
      billingCurrencyId: null,
      clicked: false,
      isButtonLoading: false,
      isLoading: true,
      additionalContactsVisible: false,
      additionalContacts: [],
      invoicingLists: {},
      showDetailsForm: false,
      selectedDetailsId: null,
      selectedInvoiceType: null,
    };
  },
  computed: {
    ...mapState('account', {
      company: 'companyName',
      first: 'firstName',
      last: 'lastName',
    }),
    ...mapState('campaigns', [
      'selectedAdvertiser',
      'campaign',
      'order',
      'currencies',
    ]),
    ...mapGetters('campaigns', {
      countries: campaignTypes.GET_COUNTRIES_LIST,
      isManagedCampaign: campaignTypes.GET_IS_MANAGED_CAMPAIGN,
      isCampaignReadonly: campaignTypes.GET_IS_CAMPAIGN_READONLY,
    }),
    additionalContactsTotal() {
      return this.additionalContacts.length;
    },
    isValid() {
      if (this.isButtonLoading) return true;
      return this.isCompanyNameValid && this.isAddressLine1Valid && this.isAddressTownCityValid
      && this.isAddressCountryIdValid && this.isAddressPostalCodeValid && this.isCompanyRegistrationNumberValid
      && this.isAccountsContactFirstNameValid && this.isAccountsContactEmailValid;
    },
    isCompanyNameValid() {
      return this.alias !== null && this.alias !== '';
    },
    isAddressLine1Valid() {
      return this.addressLine1 !== null && this.addressLine1 !== '';
    },
    isAddressTownCityValid() {
      return this.addressTownCity !== null && this.addressTownCity !== '';
    },
    isAddressCountryIdValid() {
      return this.addressCountryId !== null && this.addressCountryId !== '';
    },
    isAddressPostalCodeValid() {
      return this.addressPostalCode !== null && this.addressPostalCode !== '';
    },
    isCompanyRegistrationNumberValid() {
      return this.companyRegistrationNumber !== null && this.companyRegistrationNumber !== '';
    },
    isAccountsContactFirstNameValid() {
      return this.contactFirstName !== null && this.contactFirstName !== '';
    },
    isAccountsContactLastNameValid() {
      return this.contactLastName !== null && this.contactLastName !== '';
    },
    isAccountsContactEmailValid() {
      return this.accountsContactEmail !== null && this.accountsContactEmail !== '' && this.isEmailValid(this.accountsContactEmail);
    },
    getCampaignCurrency() {
      const currency = this.currencies
        .find((currencySet) => currencySet.id === this.order.currencyId);
      return currency.name;
    },
  },
  methods: {
    ...mapActions('account', [
      accountTypes.FETCH_INVOICING_DETAILS,
      accountTypes.UPDATE_INVOICING_DETAILS,
    ]),
    ...mapActions('campaigns', [
      campaignTypes.SUBMIT_CAMPAIGN,
      campaignTypes.CREATE_ADVERTISER,
      campaignTypes.PATCH_CAMPAIGN,
      campaignTypes.FETCH_ADVERTISER,
    ]),
    ...mapMutations([
      types.SET_GLOBAL_MESSAGE,
    ]),
    setUser() {
      this.companyName = this.company;
      this.firstName = this.first;
      this.lastName = this.last;
    },
    setInvoicingDetails(details) {
      const {
        addressLine1,
        addressLine2,
        townCity,
        postcode,
        countryId,
        companyRegistrationNumber,
        contactFirstName,
        contactLastName,
        contactEmail,
        xeroContactId,
        additionalContacts,
        id,
        billingCurrencyId,
        alias,
      } = details;
      this.addressLine1 = addressLine1;
      this.addressLine2 = addressLine2;
      this.addressTownCity = townCity;
      this.addressPostalCode = postcode;
      this.addressCountryId = countryId;
      this.companyRegistrationNumber = companyRegistrationNumber;
      this.contactFirstName = contactFirstName;
      this.contactLastName = contactLastName;
      this.accountsContactEmail = contactEmail;
      this.poNumber = this.campaign.poNumber ? this.campaign.poNumber : null;
      this.xeroId = xeroContactId;
      this.additionalContacts = additionalContacts;
      this.selectedDetailsId = id;
      this.billingCurrencyId = billingCurrencyId;
      this.alias = alias || this.companyName;
      if (this.additionalContacts.length !== 0) this.additionalContactsVisible = true;
    },
    async submitDetails() {
      if (this.clicked) {
        return;
      }
      this.clicked = true;
      this.isButtonLoading = true;
      // this.additionalContacts = this.additionalContacts.filter((contact) => Object.values(contact).every((prop) => prop !== null));
      try {
        const payload = {
          addressLine1: this.addressLine1,
          addressLine2: this.addressLine2,
          townCity: this.addressTownCity,
          postcode: this.addressPostalCode,
          countryId: this.addressCountryId,
          companyRegistrationNumber: this.companyRegistrationNumber,
          contactFirstName: this.contactFirstName,
          contactLastName: this.contactLastName,
          contactEmail: this.accountsContactEmail,
          additionalContacts: this.additionalContacts,
          billingCurrencyId: this.billingCurrencyId,
          alias: this.alias,
        };
        const invoicingDetails = await this.UPDATE_INVOICING_DETAILS(
          {
            type: this.selectedInvoiceType,
            selectedDetailsId: this.selectedDetailsId,
            payload,
          },
        );
        const campaignPayload = {
          poNumber: this.poNumber !== null ? this.poNumber : null,
          invoicingDetailsId: invoicingDetails.id,
        };
        await this.PATCH_CAMPAIGN(campaignPayload);
        if (!this.isLive) {
          await this.SUBMIT_CAMPAIGN();
          if (!this.editMode) {
            this.$router.push('/dashboard');
          }
        }
        this.isButtonLoading = false;
        this.clicked = false;
      } catch (exception) {
        this.isButtonLoading = false;
        this.clicked = false;
      }
    },
    async updateInvoicingDetails() {
      if (this.clicked) {
        return;
      }
      this.clicked = true;
      this.isButtonLoading = true;
      this.additionalContacts = this.additionalContacts.filter((contact) => Object.values(contact).every((prop) => prop !== null));

      const campaignPayload = {
        poNumber: this.poNumber,
      };
      if (this.poNumber !== null) {
        if (this.isLive) return this.PATCH_CAMPAIGN(campaignPayload);
        await this.PATCH_CAMPAIGN(campaignPayload);
      }

      if (this.selectedAdvertiser.id === -1) {
        await this.CREATE_ADVERTISER(this.selectedAdvertiser.name);
      }
      const payload = {
        addressLine1: this.addressLine1,
        addressLine2: this.addressLine2,
        townCity: this.addressTownCity,
        postcode: this.addressPostalCode,
        countryId: this.addressCountryId,
        companyRegistrationNumber: this.companyRegistrationNumber,
        contactFirstName: this.contactFirstName,
        contactLastName: this.contactLastName,
        contactEmail: this.accountsContactEmail,
        additionalContacts: this.additionalContacts,
      };
      await this.UPDATE_INVOICING_DETAILS(payload)
        .then(() => {
          this.SET_GLOBAL_MESSAGE({
            message: 'Invoicing details updated',
            type: 'info',
          });
          this.isButtonLoading = false;
          this.clicked = false;
          this.$emit('submitted');
        })
        .catch(() => {
          this.isButtonLoading = false;
          this.clicked = false;
        });
    },
    isEmailValid(email) {
      const regex = new RegExp('^(([^<>()\\[\\]\\.,;:\\s@"]+(\\.[^<>()\\[\\]\\.,;:\\s@"]+)*)|(".+"))@((\\[[0-9]{1,3}\\.[0-9]{1,3}\\.[0-9]{1,3}\\.[0-9]{1,3}])|(([a-zA-Z\\-0-9]+\\.)+[a-zA-Z]{2,24}))$');
      return (email === null)
        ? null : !!(regex.test(email));
    },
    getAdditionalContactsIndex(contact) {
      return this.additionalContacts.indexOf(contact);
    },
    addAdditionalContact() {
      this.additionalContacts.push({
        firstName: '',
        lastName: '',
        email: null,
      });
    },
    removeAdditionalContact(contact) {
      this.additionalContacts.splice(this.getAdditionalContactsIndex(contact), 1);
    },
    resetDetailsForm() {
      this.addressLine1 = '';
      this.addressLine2 = '';
      this.addressTownCity = '';
      this.addressPostalCode = '';
      this.addressCountryId = '';
      this.companyRegistrationNumber = '';
      this.contactFirstName = '';
      this.contactLastName = '';
      this.accountsContactEmail = null;
      this.poNumber = null;
      this.xeroId = '';
      this.additionalContacts = [];
      this.selectedDetailsId = null;
      // this.billingCurrencyId = this.order.currencyId;
    },
    updateDetailsSelected(payload) {
      this.showDetailsForm = payload;
    },
    addNewInvoicingDetails() {
      this.resetDetailsForm();
      this.updateDetailsSelected(true);
    },
    updateSelectionType(payload) {
      this.selectedInvoiceType = payload;
    },
  },
  async created() {
    this.billingCurrencyId = this.order.currencyId;

    const { advertiserId } = this.$route.params;

    if (this.isCampaignReadonly) {
      await this.FETCH_ADVERTISER(advertiserId);
    }

    if (this.selectedAdvertiser.id !== -1) {
      await this.FETCH_INVOICING_DETAILS({ isManagedCampaign: this.isManagedCampaign, accountId: this.selectedAdvertiser.accountId })
        .then((detailsList) => {
          this.invoicingLists = detailsList;
          this.isLoading = false;
        });
      this.setUser();
    } else {
      this.isLoading = false;
    }
  },
};
</script>
<style scoped>
.card-options-collapse{
  cursor: pointer;
}
.additional-contact-form{
  min-height: 250px;
}
.tab-content .campaign-invoicing-submit{
  margin-bottom: 25px;
}
</style>
